<template>
  <div class="v-bar-chart-table">

    <div>

      <table class="v-bar-chart-table__header">
        <tbody>
          <tr>
            <td>
              <table>
                <tbody>
                  <tr>
                    <td>
                      {{ formatNumber(minValue < 0 ? minValue : 0, props.numberFormatOptions) }} </td>
                    <td>
                      {{ formatNumber(maxValue, props.numberFormatOptions) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>


    <div>
      <table>
        <HeadlessList v-model:hover="props.hover" :items="props.items" :select-mode="SelectMode.None"
          :allow-unselect="true">
          <template #default="{ item, onItemClick, isHover }">
            <tbody :ref="(el) => setItemRef(el as Element | null, item)" tabindex="0" :class="{
              'v-bar-chart-table__item--hover': isHover,
              'x-highlight': props.highlight && item === props.highlight
            }" @click="onClick($event, item)" @contextmenu.prevent="onClick($event, item)"
              @keydown.enter="onKeyEnter(item)" @mouseover="onMouseOver(item)" @mouseout="onMouseOut(item)">
              <template v-if="props.twoRowsLayout">
                <tr>
                  <td class="v-bar-chart-table__col-name">
                    {{ props.labelFunc(item) }}
                  </td>
                  <td class="v-bar-chart-table__col-value">
                    {{
                      formatNumber(props.valueFunc(item),
                        props.numberFormatOptions) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="2" class="v-bar-chart-table__col-bar">
                    <ProgressBar :color-func="props.barColorFunc" :value="props.valueFunc(item)" :min="minValue"
                      :max="maxValue" :show-value="false"
                      :tooltip-func="(value: number) => formatNumber(value, props.numberFormatOptions)" />
                  </td>
                </tr>
              </template>

              <tr v-else>
                <td class="v-bar-chart-table__col-name">
                  {{ props.labelFunc(item) }}
                </td>
                <td class="v-bar-chart-table__col-bar">
                  <ProgressBar :color-func="props.barColorFunc" :value="props.valueFunc(item)" :min="minValue"
                    :max="maxValue" :show-value="false"
                    :tooltip-func="(value: number) => formatNumber(value, props.numberFormatOptions)" />
                </td>
                <td class="v-bar-chart-table__col-value">
                  {{
                    formatNumber(props.valueFunc(item),
                      props.numberFormatOptions) }}
                </td>
              </tr>
            </tbody>
          </template>
        </HeadlessList>
      </table>
    </div>
  </div>
</template>


<script setup lang="ts">
import { formatNumber, FormatNumberOptions, ProgressBar, HeadlessList, SelectMode } from 'gertrud';

import { computed } from 'vue';


export interface BarChartTableItemClickEvent {
  item: any,
  mouseEvent: MouseEvent
}

const props = withDefaults(defineProps<{
  barColorFunc?: (value: number) => string
  items: object[]
  minOverride?: number
  maxOverride?: number
  hover?: object | null
  highlight?: object | null
  labelFunc: (item: any) => string
  numberFormatOptions?: FormatNumberOptions
  valueFunc: (item: any) => number
  twoRowsLayout?: boolean
}>(), {
  items: () => [],
  numberFormatOptions: () => ({}),
  twoRowsLayout: false,

})

const emit = defineEmits<{
  'update:hover': [item: object | null]
  'update:selected': [items: object[]]
  'item-click': [payload: BarChartTableItemClickEvent]
}>();



const maxValue = computed(() => {

  if (typeof props.maxOverride === "number") return props.maxOverride;

  return Math.max(...props.items.map(i => props.valueFunc(i)));
})


const minValue = computed(() => {

  if (typeof props.minOverride === "number") return props.minOverride;

  return Math.min(...props.items.map(i => props.valueFunc(i)));
})

function onKeyEnter(item: any) {
  emit('update:selected', [item]);
}

function onClick(event: MouseEvent, item: any) {
  emit('update:selected', [item]);

  emit('item-click', { item: item, mouseEvent: event });
}
function onMouseOver(item: any) {
  emit('update:hover', item);
}

function onMouseOut(item: any) {
  emit('update:hover', null);
}

// #region Scroll to item
const itemElements = new Map<any, Element>();

function setItemRef(el: Element | null, item: any) {
  if (el) itemElements.set(item, el);
  else itemElements.delete(item);
}

function scrollToItem(item: any) {
  itemElements.get(item)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

defineExpose({ scrollToItem });
// #endregion Scroll to item
</script>
